<script setup lang="ts">
  import { reactiveOmit } from '@vueuse/core';
  import type { VariantProps } from 'class-variance-authority';
  import type { ToggleGroupItemProps } from 'reka-ui';
  import { ToggleGroupItem, useForwardProps } from 'reka-ui';
  import type { HTMLAttributes } from 'vue';
  import { inject } from 'vue';

  import { toggleVariants } from '@ui/components/toggle';
  import { cn } from '@ui/utils/utils';

  type ToggleGroupVariants = VariantProps<typeof toggleVariants> & {
    spacing?: number;
  };

  const props = defineProps<
    ToggleGroupItemProps & {
      class?: HTMLAttributes['class'];
      variant?: ToggleGroupVariants['variant'];
      size?: ToggleGroupVariants['size'];
    }
  >();

  const context = inject<ToggleGroupVariants>('toggleGroup');

  const delegatedProps = reactiveOmit(props, 'class', 'size', 'variant');
  const forwardedProps = useForwardProps(delegatedProps);
</script>

<template>
  <ToggleGroupItem
    v-slot="slotProps"
    data-slot="toggle-group-item"
    :data-variant="context?.variant || variant"
    :data-size="context?.size || size"
    :data-spacing="context?.spacing"
    v-bind="forwardedProps"
    :class="
      cn(
        toggleVariants({
          variant: context?.variant || variant,
          size: context?.size || size,
        }),
        'min-w-fit flex-1 shrink-0 focus:z-10 focus-visible:z-10',
        'data-[spacing=0]:rounded-none data-[spacing=0]:shadow-none data-[spacing=0]:first:rounded-l-sm data-[spacing=0]:last:rounded-r-sm data-[spacing=0]:data-[variant=outline]:border-l-0 data-[spacing=0]:data-[variant=outline]:first:border-l',
        props.class,
      )
    "
  >
    <slot v-bind="slotProps" />
  </ToggleGroupItem>
</template>
